import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {usePwa} from '@/shared/lib/pwa';
import {Icon} from '@/shared/ui/icons';
import {AppText, useLinkText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * "Доступно обновление" — the strip that appears when the service worker has a newer
 * bundle downloaded and waiting.
 *
 * It is an OFFER, not an announcement, and that is the whole design. The worker
 * deliberately refuses to activate on its own (web/public/sw.js does not call
 * skipWaiting on install) because a bundle that swaps itself in mid-session takes a
 * half-filled personnel form with it. So the new version sits waiting, this strip says
 * so, and the reload happens when the user taps — at a moment they chose.
 *
 * Teal rather than amber: unlike the offline strip below it, this is good news.
 * Renders nothing on native, where `usePwa()` is inert (see shared/lib/pwa.ts).
 */
const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing(2),
    paddingBottom: spacing(2),
    paddingHorizontal: spacing(4),
  },
  action: {paddingVertical: spacing(1), paddingHorizontal: spacing(2)},
});

export function UpdateBanner({topInset}: {topInset: number}) {
  const {t} = useTranslation();
  const p = useTheme();
  const link = useLinkText();
  const {updateReady, applyUpdate} = usePwa();

  if (!updateReady) {
    return null;
  }

  return (
    <View
      accessibilityRole="alert"
      style={[
        styles.bar,
        {backgroundColor: p.accent, paddingTop: topInset + spacing(2)},
      ]}>
      <Icon name="refresh" size={14} color={p.primary} />
      <AppText variant="caption" color={p.primary} numberOfLines={1}>
        {t('pwa.updateReady')}
      </AppText>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t('pwa.updateAction')}
        onPress={applyUpdate}
        hitSlop={8}
        style={styles.action}>
        <AppText variant="label" style={link}>
          {t('pwa.updateAction')}
        </AppText>
      </Pressable>
    </View>
  );
}
